// The app shell: sidebar, top bar, offline banner, the routed screen, the global keyboard shortcuts and the idle lock.
import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useIdleLock } from "../session/idle-lock";
import { useOneStore } from "../session/one-store";
import { lockScreen, useSessionView } from "../session/store";
import { ErrorBoundary } from "../ui/ErrorBoundary";
import { inBurst, isTypingTarget as isTyping } from "./key-burst";
import { OfflineBanner, SuspendedBanner } from "./OfflineBanner";
import { ShortcutsOverlay } from "./Shortcuts";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";

/** A modal dialog owns the keyboard while it is open: `g t`, `/`, `?` and Shift+L must not act behind it (adversarial review 16 Sep 2026, L9). */
const modalOpen = (): boolean => document.querySelector('[role="dialog"][aria-modal="true"]') !== null;

export function Shell() {
  const [help, setHelp] = useState(false);
  const search = useRef<HTMLInputElement | null>(null);
  const chord = useRef<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSessionView();
  useIdleLock(session);
  // Whether the practice has one store is read once here, as the session opens, so a screen opened next already knows it and
  // does not offer a choice of branch only to take it away (change 13; session/one-store.ts).
  useOneStore();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (modalOpen()) return;
      if (isTyping(e.target)) { if (e.key === "Escape") (e.target as HTMLElement).blur(); return; }
      // A barcode scanner's keys, a few milliseconds apart, are a code, never a shortcut: a scan of CL-BIOT-6 landing on a button
      // carried Shift+L and locked the session (the review of 18 Oct 2026, D1-2; TC-IDENTITY-109). A person's keys still act.
      if (inBurst(e)) { chord.current = null; return; }
      if (chord.current === "g") {
        chord.current = null;
        if (e.key === "t") { e.preventDefault(); navigate("/today"); return; }
        if (e.key === "s") { e.preventDefault(); navigate("/settings/general"); return; }
        if (e.key === "b") { e.preventDefault(); document.querySelector<HTMLButtonElement>("button.branch")?.click(); return; }
      }
      if (e.key === "?") { e.preventDefault(); setHelp(true); return; }
      if (e.key === "/") { e.preventDefault(); search.current?.focus(); return; }
      if (e.key === "g") { chord.current = "g"; setTimeout(() => { chord.current = null; }, 1000); return; }
      if (e.key === "L" && e.shiftKey) { e.preventDefault(); void lockScreen(); return; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  return (
    <div className="app">
      <Sidebar />
      <div className="main">
        <TopBar onSearchRef={(el) => { search.current = el; }} />
        <OfflineBanner />
        <SuspendedBanner />
        {/* A screen that throws shows the error panel here, the sidebar still usable; another page tries again (review S8). */}
        <ErrorBoundary resetKey={location.pathname + location.search}>
          <Outlet />
        </ErrorBoundary>
      </div>
      {help && <ShortcutsOverlay onClose={() => setHelp(false)} />}
    </div>
  );
}
